import { call, put, select, takeLatest } from 'redux-saga/effects'
import { getPcrIdSelector } from '../../../home/selectors'
import { throwPcrException } from '../../../pcr/store/pcr.actions'
import { IPcr } from '../../../pcr/store/pcr.types'
import { getServiceIdSelector } from '../../../user/selectors'
import { getVentricularAssistDeviceSelector } from '../selectors'
import { setAddToTimeLine } from '../store/ventricularAssistDevice.actions'
import { VentricularAssistDeviceConstants } from '../store/ventricularAssistDevice.constants'
import { IVentricularAssistDeviceState } from '../store/ventricularAssistDevice.types'
import { addVentricularAssistDeviceItem } from '../../../timeline/store/timeline.actions'
import { getProcedureStateSelector } from '../../../../components/procedure/selectors'
import { PcrDocsService } from '../../../../services/pcrDocs.service'
import { performWithMutexHOG } from '../../../../services/mutexUtility.service'
import { handleNemsisCodeError, getUpdatedProcedureStateWithNemsisCode } from '../../../../utilities/nemsisCode.util'
import { INemsisSnomedLookup } from '../../../../components/procedure/store/procedure.types'

const processNemsisCode = (
  ventricularAssistDeviceState: IVentricularAssistDeviceState,
  nemsisCodeOptions: INemsisSnomedLookup[]
) => {
  try {
    return getUpdatedProcedureStateWithNemsisCode({
      procedureState: ventricularAssistDeviceState,
      actionField: () => null,
      filter1Field: () => null,
      filter2Field: () => null,
      filter3Field: () => null,
      nemsisCodeOptions,
      includeSnomedId: true,
    })
  } catch (error) {
    return handleNemsisCodeError(error, ventricularAssistDeviceState)
  }
}

export function* saveVentricularAssistDevice() {
  let ventricularAssistDeviceState: IVentricularAssistDeviceState = yield select(getVentricularAssistDeviceSelector)

  const serviceId: string = yield select(getServiceIdSelector)
  const pcrDocsService = new PcrDocsService(serviceId)
  const pcrID: string = yield select(getPcrIdSelector)
  const { nemsisCodeOptions } = yield select(getProcedureStateSelector)

  if (!pcrID) {
    // todo: add to audit log
    return
  }

  ventricularAssistDeviceState = processNemsisCode(ventricularAssistDeviceState, nemsisCodeOptions)

  try {
    const pcrDoc: IPcr = yield call([pcrDocsService, pcrDocsService.getPCR], pcrID)

    const vadList = pcrDoc.patient.ventricularAssistDeviceStateList || []
    const existingIndex = vadList.findIndex((data) => data.key === ventricularAssistDeviceState.key)

    if (existingIndex !== -1) {
      vadList[existingIndex] = ventricularAssistDeviceState
    } else {
      vadList.push(ventricularAssistDeviceState)
    }

    const pcrDocUpdated = {
      ...pcrDoc,
      patient: {
        ...pcrDoc.patient,
        ventricularAssistDeviceStateList: vadList,
      },
    }
    yield call([pcrDocsService, pcrDocsService.savePCR], pcrDocUpdated)
    yield put(setAddToTimeLine(true))
    yield put(addVentricularAssistDeviceItem())
  } catch (e) {
    yield put(throwPcrException(`saveVentricularAssistDevice() error: ${e}`))
  }
}

export function* saveVentricularAssistDeviceSaga() {
  yield takeLatest(
    [
      VentricularAssistDeviceConstants.SAVE_VENTRICULAR_ASSIST_DEVICE,
      VentricularAssistDeviceConstants.SET_DATE,
      VentricularAssistDeviceConstants.SET_TIME,
      VentricularAssistDeviceConstants.SET_DATE_TIME,
      VentricularAssistDeviceConstants.SET_PTA,
      VentricularAssistDeviceConstants.SET_ASSESSED_BY,
      VentricularAssistDeviceConstants.SET_ROLE_PERFORMING,
      VentricularAssistDeviceConstants.SET_ROLE_PERFORMING_PNNV,
      VentricularAssistDeviceConstants.SET_PROCEDURE_PERFORMED_BY_ANOTHER_UNIT,
      VentricularAssistDeviceConstants.SET_PROCEDURE_PERFORMED_BY_ANOTHER_UNIT_PNNV,
      VentricularAssistDeviceConstants.SET_PROCEDURE_COMPLICATIONS,
      VentricularAssistDeviceConstants.SET_RESPONSE,
      VentricularAssistDeviceConstants.SET_RESPONSE_PNNV,
      VentricularAssistDeviceConstants.SET_AUTHORIZATION,
      VentricularAssistDeviceConstants.SET_INDICATION,
      VentricularAssistDeviceConstants.SET_PULSATILITY_INDEX,
      VentricularAssistDeviceConstants.SET_AUSCULTATED,
      VentricularAssistDeviceConstants.SET_MAKE_MODEL_VAD,
      VentricularAssistDeviceConstants.SET_MAKE_MODEL_IMPELLA,
      VentricularAssistDeviceConstants.SET_P_LEVEL,
      VentricularAssistDeviceConstants.SET_SPEED_RPMS,
      VentricularAssistDeviceConstants.SET_FLOW_TYPE,
      VentricularAssistDeviceConstants.SET_CONTROLLER_NUMBER,
      VentricularAssistDeviceConstants.SET_MOTOR_CURRENT_MA,
      VentricularAssistDeviceConstants.SET_MOTOR_CURRENT_WAVE_FORM_CHARACTERISTICS,
      VentricularAssistDeviceConstants.SET_PLACEMENT_SIGNAL_MMHG,
      VentricularAssistDeviceConstants.SET_PLACEMENT_SIGNAL_WAVE_FORM_CHARACTERISTICS,
      VentricularAssistDeviceConstants.SET_PLACEMENT_CONFIRMED_BY,
      VentricularAssistDeviceConstants.SET_CATHETER_SIZE,
      VentricularAssistDeviceConstants.SET_CATHETER_DEPTH_CM,
      VentricularAssistDeviceConstants.SET_CATHETER_SERIAL_NUMBER,
      VentricularAssistDeviceConstants.SET_INSERT_LOCATION,
      VentricularAssistDeviceConstants.SET_PLACEMENT_LOCATION,
      VentricularAssistDeviceConstants.SET_SITE_ASSESSMENT,
      VentricularAssistDeviceConstants.SET_LEG_IMMOBILIZED,
      VentricularAssistDeviceConstants.SET_CATHETER_SUTURED_IN_PLACE,
      VentricularAssistDeviceConstants.SET_DRESSING_CONDITION,
      VentricularAssistDeviceConstants.SET_PULSES_DISTAL_TO_INSERTION_SITE,
      VentricularAssistDeviceConstants.SET_ANCHORING_RING_LOCKED,
      VentricularAssistDeviceConstants.SET_HEAD_OF_BED_AT_30_DEGREES,
      VentricularAssistDeviceConstants.SET_ANTICOAGULATION,
      VentricularAssistDeviceConstants.SET_ANTIDYSRHYTHMIA,
      VentricularAssistDeviceConstants.SET_PURGE_SOLUTION_DEXTROSE_PERCENT,
      VentricularAssistDeviceConstants.SET_PURGE_SOLUTION_HEPARIN_IU_ML,
      VentricularAssistDeviceConstants.SET_PURGE_SOLUTION_FLOW_ML_HR,
      VentricularAssistDeviceConstants.SET_PRESSURE_BAG_USED,
      VentricularAssistDeviceConstants.SET_PURGE_PRESSURE_MMHG,
      VentricularAssistDeviceConstants.SET_FLOW_L_MIN,
      VentricularAssistDeviceConstants.SET_POWER_WATTS,
      VentricularAssistDeviceConstants.SET_POWER_TYPE,
      VentricularAssistDeviceConstants.SET_BATTERY_STATUS,
      VentricularAssistDeviceConstants.SET_BACKUP_OPTIONS,
      VentricularAssistDeviceConstants.SET_ALARMS,
      VentricularAssistDeviceConstants.SET_VAD_COORDINATOR_NOTIFICATION,
      VentricularAssistDeviceConstants.SET_INFLOW_CANNULA_SITE_ASSESSMENT,
      VentricularAssistDeviceConstants.SET_OUTFLOW_CANNULA_SITE_ASSESSMENT,
    ],
    performWithMutexHOG,
    saveVentricularAssistDevice
  )
}
